import React, { useEffect, useState, useRef } from "react";

/**
 * WorldMap — animated hero background with pulsing dots at global miner locations.
 * Pure SVG, no dependencies. Cheap on CPU.
 */

// Approximate lat/lng of key global mining hubs, mapped to SVG viewBox [0,0,1000,500]
// projection: simple equirectangular. Coords derived from lat/lng.
const HUBS = [
    { name: "Mumbai",    x: 690, y: 240 },
    { name: "Bengaluru", x: 680, y: 265 },
    { name: "Dubai",     x: 620, y: 235 },
    { name: "Singapore", x: 750, y: 285 },
    { name: "Tokyo",     x: 820, y: 210 },
    { name: "Shanghai",  x: 790, y: 225 },
    { name: "Sydney",    x: 850, y: 350 },
    { name: "London",    x: 500, y: 175 },
    { name: "Frankfurt", x: 515, y: 180 },
    { name: "Amsterdam", x: 510, y: 170 },
    { name: "Paris",     x: 500, y: 185 },
    { name: "Stockholm", x: 530, y: 145 },
    { name: "Moscow",    x: 570, y: 155 },
    { name: "Istanbul",  x: 570, y: 200 },
    { name: "Riyadh",    x: 605, y: 235 },
    { name: "Lagos",     x: 500, y: 285 },
    { name: "Cape Town", x: 540, y: 380 },
    { name: "São Paulo", x: 335, y: 350 },
    { name: "Buenos Aires", x: 320, y: 385 },
    { name: "Mexico City", x: 220, y: 280 },
    { name: "New York",  x: 275, y: 210 },
    { name: "Los Angeles", x: 175, y: 225 },
    { name: "Toronto",   x: 265, y: 195 },
    { name: "Seoul",     x: 815, y: 220 },
    { name: "Bangkok",   x: 740, y: 270 },
    { name: "Karachi",   x: 655, y: 230 },
    { name: "Cairo",     x: 570, y: 220 },
    { name: "Nairobi",   x: 585, y: 310 },
    { name: "Vancouver", x: 175, y: 190 },
    { name: "Jakarta",   x: 755, y: 305 },
].filter((h) => typeof h.x === "number" && typeof h.y === "number");

export default function WorldMap({ className = "" }) {
    const [active, setActive] = useState(new Set());
    const [pulses, setPulses] = useState([]);
    const pulseIdRef = useRef(0);

    useEffect(() => {
        const tick = setInterval(() => {
            // Random subset of hubs are "active" this tick
            const nextActive = new Set();
            const n = 6 + Math.floor(Math.random() * 5);
            for (let i = 0; i < n; i++) {
                nextActive.add(Math.floor(Math.random() * HUBS.length));
            }
            setActive(nextActive);

            // Add a new pulse ring on one active hub
            const idx = Math.floor(Math.random() * HUBS.length);
            const hub = HUBS[idx];
            const pulseId = ++pulseIdRef.current;
            setPulses((p) => [...p.slice(-9), { id: pulseId, x: hub.x, y: hub.y }]);
            setTimeout(() => setPulses((p) => p.filter((pp) => pp.id !== pulseId)), 2200);
        }, 1400);
        return () => clearInterval(tick);
    }, []);

    return (
        <svg
            viewBox="0 0 1000 500"
            className={`w-full h-full ${className}`}
            preserveAspectRatio="xMidYMid slice"
            aria-hidden="true"
        >
            <defs>
                <radialGradient id="wm-pulse" cx="50%" cy="50%" r="50%">
                    <stop offset="0%" stopColor="#00E5FF" stopOpacity="0.55" />
                    <stop offset="70%" stopColor="#2563EB" stopOpacity="0" />
                </radialGradient>
                <filter id="wm-glow">
                    <feGaussianBlur stdDeviation="1.5" />
                </filter>
            </defs>

            {/* Grid backdrop */}
            <g opacity="0.15" stroke="#3B82F6" strokeWidth="0.35" fill="none">
                {Array.from({ length: 20 }).map((_, i) => (
                    <line key={`h-${i}`} x1="0" y1={25 * i} x2="1000" y2={25 * i} />
                ))}
                {Array.from({ length: 40 }).map((_, i) => (
                    <line key={`v-${i}`} x1={25 * i} y1="0" x2={25 * i} y2="500" />
                ))}
            </g>

            {/* Dotted continents (highly stylised via clusters of small circles) */}
            <g fill="#1E40AF" opacity="0.35">
                {CONTINENT_DOTS.map((d, i) => (
                    <circle key={i} cx={d[0]} cy={d[1]} r="1.4" />
                ))}
            </g>

            {/* Connection lines between hubs */}
            <g stroke="#2563EB" strokeWidth="0.4" opacity="0.35" fill="none">
                {HUB_LINES.map((l, i) => (
                    <line key={i} x1={HUBS[l[0]]?.x} y1={HUBS[l[0]]?.y} x2={HUBS[l[1]]?.x} y2={HUBS[l[1]]?.y} />
                ))}
            </g>

            {/* Pulse rings */}
            {pulses.map((p) => (
                <circle key={p.id} cx={p.x} cy={p.y} r="3" fill="none" stroke="#00E5FF" strokeWidth="1">
                    <animate attributeName="r" from="3" to="26" dur="2.2s" fill="freeze" />
                    <animate attributeName="opacity" from="0.9" to="0" dur="2.2s" fill="freeze" />
                </circle>
            ))}

            {/* Hub dots */}
            {HUBS.map((hub, i) => (
                <g key={i}>
                    <circle cx={hub.x} cy={hub.y} r={active.has(i) ? 4 : 2}
                            fill={active.has(i) ? "#00E5FF" : "#3B82F6"}
                            filter={active.has(i) ? "url(#wm-glow)" : undefined}>
                        {active.has(i) && <animate attributeName="r" values="4;5.5;4" dur="1.4s" repeatCount="indefinite" />}
                    </circle>
                </g>
            ))}
        </svg>
    );
}

// Precomputed dot clusters roughly outlining continents (hand-tuned, low density)
const CONTINENT_DOTS = [
    // North America
    [140,180],[155,175],[170,178],[185,180],[200,185],[215,190],[230,195],[245,200],[260,205],[275,210],[290,215],
    [160,200],[175,205],[190,208],[205,210],[220,215],[235,218],[250,220],[265,225],[280,228],
    [155,220],[170,225],[185,228],[200,230],[215,233],[230,235],[245,240],
    [170,245],[185,248],[200,250],[215,253],[230,255],
    // Central + South America
    [235,265],[220,275],[210,285],[240,290],[250,300],[275,310],[285,325],[295,340],[300,355],[305,370],[300,385],[295,400],[290,415],
    [315,355],[325,365],[335,375],[345,385],[350,395],
    // Europe
    [485,150],[500,152],[515,150],[530,148],[545,150],[560,155],
    [485,168],[500,168],[515,170],[530,170],[545,172],[560,175],[575,178],
    [485,185],[500,185],[515,188],[530,188],[545,190],[560,193],
    // Africa
    [510,225],[525,225],[540,225],[555,225],[570,225],
    [500,245],[515,248],[530,250],[545,252],[560,255],[575,258],
    [495,270],[510,275],[525,280],[540,285],[555,290],
    [500,300],[515,308],[530,315],[545,320],[560,318],
    [510,340],[525,350],[540,360],[555,368],[540,378],
    // Middle East + Central Asia
    [590,205],[605,210],[620,215],[635,220],[650,225],[665,225],
    [605,235],[620,240],[635,245],[650,250],[665,250],[680,248],
    // South Asia
    [670,255],[685,260],[700,265],[675,275],[690,280],[705,285],
    // South-East Asia
    [720,270],[735,275],[750,280],[765,285],[780,290],
    [730,295],[745,300],[760,305],[775,308],
    // East Asia
    [770,215],[785,215],[800,215],[815,215],
    [770,230],[785,232],[800,232],[815,232],
    [770,248],[785,250],[800,250],[815,250],
    // Australia + Oceania
    [820,330],[835,335],[850,340],[865,345],[840,355],[855,360],[820,355],
];

// Connections between hubs to imply DePIN network relays
const HUB_LINES = [
    [0,3],[0,2],[3,5],[5,4],[0,7],[7,8],[8,10],[10,11],[11,12],[12,13],
    [3,15],[15,16],[16,17],[17,18],[7,20],[20,21],[21,22],[22,29],
    [23,4],[24,3],[25,2],[26,14],[27,15],[6,29],[19,20],[28,22],
];
